<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useLoadWithRetry } from 'dashboard/composables/loadWithRetry';
import { useMessageContext } from '../provider.js';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});

const { isLoaded, hasError, loadWithRetry } = useLoadWithRetry({
  max_retry: 4,
  backoff: 800,
});

const showGallery = ref(false);
const isLoading = computed(() => !isLoaded.value && !hasError.value);

const { filteredCurrentChatAttachments } = useMessageContext();

const displayUrl = computed(
  () => props.attachment?.thumbUrl || props.attachment?.dataUrl
);

onMounted(() => {
  if (displayUrl.value) {
    loadWithRetry(displayUrl.value, props.attachment?.id);
  }
});

watch(displayUrl, newUrl => {
  if (newUrl && !isLoaded.value && !hasError.value) {
    loadWithRetry(newUrl, props.attachment?.id);
  }
});
</script>

<template>
  <div
    class="size-[72px] overflow-hidden contain-content rounded-xl cursor-pointer"
    @click="showGallery = true"
  >
    <!-- Loading skeleton -->
    <div
      v-if="isLoading"
      class="flex items-center justify-center size-full bg-n-alpha-2 animate-pulse"
    >
      <Icon icon="i-lucide-image" class="size-5 text-n-slate-9" />
    </div>
    <div
      v-else-if="hasError"
      class="flex flex-col items-center justify-center gap-1 text-xs text-center rounded-lg size-full bg-n-alpha-1 text-n-slate-11"
    >
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      {{ $t('COMPONENTS.MEDIA.LOADING_FAILED') }}
    </div>
    <img
      v-else-if="isLoaded"
      class="object-cover w-full h-full skip-context-menu"
      :src="displayUrl"
    />
  </div>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="() => (hasError.value = true)"
    @close="() => (showGallery = false)"
  />
</template>
